import { type DefaultRootState } from '@databases/redux';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { type CloudId, type JiraServerId } from '@databases/types/confluence';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { resetJiraServerIssues } from '@databases/utils/redux/jira/actions';
import { type SimpleStore } from '@databases/utils/redux/redux';
import { sleep } from '@databases/utils/utils';

/**
 * Opens a popup where the user can authenticate their Jira account. Once the popup is closed, all loaded Jira issues from the
 * same server are discarded (so that the UI reloads them).
 * @param cloudId
 * @param serverId
 * @param authenticationPath The path (without base URL) to the authentication UI, as stored on JiraAuthenticationError.
 * @param store
 * @param errorMessage
 * @return Returns a promise that is resolved after the popup is closed.
 */
export async function authenticateJira(
  cloudId: CloudId,
  serverId: JiraServerId,
  authenticationPath: string,
  store: SimpleStore<DefaultRootState>,
  errorMessage: string
): Promise<void> {
  const baseUrl = selectBaseUrl(store.getState());
  const popup = open(`${baseUrl}${authenticationPath}`, undefined, 'popup=1');

  if (!popup) {
    throw new Error(errorMessage);
  }

  while (popup && !popup.closed) {
    await sleep(500);
  }

  store.dispatch(resetJiraServerIssues(cloudId, serverId));
  currentSnackbarState.clearMessage('jira-auth');
}
